import { useMemo, useState } from "react";
import { api, type HistoryEvent, type ProductRow } from "../api";
import { useAsync } from "../useAsync";
import { KeeperBadge } from "../components/KeeperMark";

/**
 * History: the audit log as a timeline. Everything that changed and every
 * decision that was made — by a human (resolved_by), by the Keeper, by the
 * system, or by the policy walls — with the raw payload one disclosure away.
 */
type Origin = "all" | "human" | "keeper" | "system";

const ORIGIN_CHIPS: Array<{ key: Origin; label: string }> = [
  { key: "all", label: "All" },
  { key: "human", label: "Humans" },
  { key: "keeper", label: "Keeper" },
  { key: "system", label: "System & policy" },
];

export function History({ product }: { product: ProductRow }) {
  const [scope, setScope] = useState<"product" | "account">("product");
  const [origin, setOrigin] = useState<Origin>("all");

  const events = useAsync(
    () => api.history({ product: scope === "product" ? product.slug : undefined, days: 90, limit: 300 }),
    [product.slug, scope]
  );

  const filtered = useMemo(
    () => (events.data ?? []).filter((e) => origin === "all" || originOf(e) === origin),
    [events.data, origin]
  );

  const byDay = useMemo(() => {
    const groups: Array<{ day: string; items: HistoryEvent[] }> = [];
    for (const e of filtered) {
      const day = new Date(e.occurred_at).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
      const last = groups[groups.length - 1];
      if (last && last.day === day) last.items.push(e);
      else groups.push({ day, items: [e] });
    }
    return groups;
  }, [filtered]);

  return (
    <div className="page" style={{ paddingTop: 26 }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <h1>History</h1>
          <span className="sub">
            Every change and decision from the last 90 days — human or AI — from the audit log, verbatim.
          </span>
        </div>
        <span style={{ flex: 1 }} />
        <div style={{ display: "flex", gap: 6 }}>
          <button type="button" className="seg" aria-pressed={scope === "product"} onClick={() => setScope("product")}>
            {product.name}
          </button>
          <button type="button" className="seg" aria-pressed={scope === "account"} onClick={() => setScope("account")}>
            Everything
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, padding: "18px 0 0", flexWrap: "wrap" }}>
        {ORIGIN_CHIPS.map((c) => (
          <button key={c.key} type="button" className="seg" aria-pressed={origin === c.key}
                  onClick={() => setOrigin(c.key)}>
            {c.label}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <span className="num" style={{ fontSize: 12, color: "var(--text-faint)", alignSelf: "center" }}>
          {filtered.length} events
        </span>
      </div>

      {events.error ? (
        <div style={{ padding: "40px 0", color: "var(--neg)" }}>{events.error}</div>
      ) : events.loading ? (
        <div style={{ padding: "40px 0", color: "var(--text-faint)" }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ marginTop: 22, border: "1px dashed var(--border-strong)", borderRadius: 12,
                      padding: "22px 24px", fontSize: 13, color: "var(--text-3)", lineHeight: 1.6 }}>
          Nothing recorded yet for this filter. Approvals, Keeper cards, proposals, policy blocks and config
          edits will all land here — the audit log records every write.
        </div>
      ) : (
        byDay.map((g) => (
          <section key={g.day} style={{ marginTop: 26 }}>
            <div className="eyebrow" style={{ paddingBottom: 8, borderBottom: "1px solid var(--border)" }}>{g.day}</div>
            {g.items.map((e) => <EventRow key={e.id} e={e} showProduct={scope === "account"} />)}
          </section>
        ))
      )}
    </div>
  );
}

function EventRow({ e, showProduct }: { e: HistoryEvent; showProduct: boolean }) {
  const [open, setOpen] = useState(false);
  const origin = originOf(e);
  const subject = subjectOf(e);
  const hasPayload = e.payload && Object.keys(e.payload).length > 0;

  return (
    <div style={{ display: "flex", gap: 14, padding: "12px 2px", borderBottom: "1px solid var(--border-hairline)" }}>
      <span className="num" style={{ width: 44, flex: "none", fontSize: 12, color: "var(--text-faint)", paddingTop: 2 }}>
        {new Date(e.occurred_at).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false })}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <ActorChip origin={origin} actor={e.actor} />
          <span style={{ fontSize: 13, color: "var(--text-2)" }}>{verbOf(e)}</span>
          {showProduct && e.product_slug && (
            <span className="eyebrow" style={{ background: "var(--surface-inset)", borderRadius: 4, padding: "2px 7px" }}>
              {e.product_slug}
            </span>
          )}
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 10, color: "var(--text-faint)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            via {e.via}
          </span>
          {hasPayload && (
            <button type="button" className="btn-link" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
              {open ? "Hide" : "Details"}
            </button>
          )}
        </div>
        {subject && (
          <div style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 4, overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {subject}
          </div>
        )}
        {open && (
          <pre className="num" style={{ margin: "8px 0 0", fontSize: 11.5, lineHeight: 1.55, color: "var(--text-3)",
                        background: "var(--surface-sunk)", border: "1px solid var(--border)", borderRadius: 8,
                        padding: "10px 12px", overflowX: "auto" }}>
            {JSON.stringify(e.payload, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

function ActorChip({ origin, actor }: { origin: Exclude<Origin, "all">; actor: string }) {
  if (origin === "keeper") {
    return <KeeperBadge />;
  }
  const human = origin === "human";
  return (
    <span style={{
      fontSize: 11.5, fontWeight: human ? 600 : 400, borderRadius: 5, padding: "2px 8px",
      color: human ? "var(--text)" : "var(--text-3)",
      background: human ? "var(--solid)" : "var(--muted-bg)",
      maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    }}>
      {actor}
    </span>
  );
}

function originOf(e: HistoryEvent): Exclude<Origin, "all"> {
  if (e.actor === "keeper") return "keeper";
  if (e.actor === "system" || e.actor === "policy" || e.via === "job") return "system";
  return "human";
}

const VERBS: Record<string, string> = {
  create_card: "raised a card",
  card_approved: "approved",
  card_rejected: "dismissed",
  card_snoozed: "snoozed",
  card_restored: "restored a card",
  product_created: "created the project",
  proposal_submitted: "proposed a spend change",
  proposal_policy_refused: "had a proposal refused by policy",
  policy_block: "blocked an approval",
  log_distribution: "logged distribution",
  mysql_sync: "synced conversions",
  oauth_start: "started a Google connect",
  oauth_connected: "granted Google access",
  oauth_select: "activated Google sources",
  semrush_connected: "connected SEMrush",
  opinly_connected: "connected Opinly",
  bing_connected: "connected Bing Webmaster",
  ahrefs_connected: "connected Ahrefs",
  clarity_connected: "connected Microsoft Clarity",
  posthog_connected: "connected PostHog",
  posthog_event_maps_updated: "updated PostHog event maps",
  connection_disabled: "disabled a connection",
  dashboard_created: "created a dashboard",
  dashboard_updated: "updated a dashboard",
  dashboard_deleted: "deleted a dashboard",
  funnel_definition_updated: "edited the funnel definition",
  api_key_created: "created an API key",
  api_key_revoked: "revoked an API key",
};

function verbOf(e: HistoryEvent): string {
  return VERBS[e.action] ?? e.action.replace(/_/g, " ");
}

function subjectOf(e: HistoryEvent): string | null {
  if (e.card_headline) return `${e.card_type ? `[${e.card_type}] ` : ""}${e.card_headline}`;
  const p = e.payload as Record<string, unknown>;
  if (typeof p?.name === "string") return p.name;
  if (typeof p?.headline === "string") return p.headline;
  if (typeof p?.reason === "string") return p.reason;
  if (Array.isArray(p?.steps)) return (p.steps as string[]).join(" → ");
  if (typeof p?.channel === "string" && typeof p?.kind === "string") return `${p.kind} on ${p.channel}`;
  return null;
}
